import { Talking } from '../network/Connection';

/**
 * Rozmowa: panel przy prawej krawędzi ekranu.
 *
 * **Pasek boczny, a nie menu pod kursorem**, i to jest różnica wobec menu
 * budynku. Tamto jest odpowiedzią na jedno kliknięcie i gaśnie przy następnym;
 * to jest **stan, w którym gra się znajduje** — trwa, dopóki trwa rozmowa,
 * i ma nad sobą licznik, który się zmienia. Menu przy kursorze musiałoby albo
 * zostać tam, gdzie kliknięto, albo chodzić za rozmówcą; jedno i drugie
 * przeszkadzałoby patrzeniu na to, co się dzieje.
 *
 * **Dwie strony, jeden kształt.** Obie widzą ten sam przebieg i mają swoje
 * kwestie do wybrania; różni je jedno — zagadujący ma wyjście, zatrzymany
 * zdanie o tym, że się nie ruszy. Dwa panele byłyby dwoma miejscami do
 * poprawienia przy pierwszej dołożonej pozycji.
 *
 * **Rozmowa idzie na przemian**, więc czekanie dotyczy obu stron: kto powiedział
 * ostatnie zdanie, ma kwestie wyszarzone, dopóki tamten nie odpowie. Rozstrzyga
 * to serwer (`talk::turn`) i przysyła gotowe `enabled` — panel czyta, czy jest
 * czym kliknąć, i nie liczy niczyjej kolejki.
 *
 * **Panel nie wie, co znaczą kwestie, i to jest wybór.** Etykiety, kolejność
 * i to, czy wolno wybrać, przychodzą z serwera przy każdej zmianie rozmowy —
 * lista w tym pliku byłaby drugim słownikiem, a pierwsza kwestia, która ruszy
 * światem, i tak musi być rozstrzygana po tamtej stronie.
 *
 * Licznik odliczany **własnym zegarem, z terminu**: serwer mówi, ile zostało,
 * a klient liczy — ta sama umowa, co przy pasku budowy i przy dobie, i ten sam
 * powód, żeby nic nie musiało jechać po drucie co sekundę. Termin **odnawia
 * się każdą kwestią**, więc przychodzi z każdą wiadomością, a nie tylko na
 * początku rozmowy.
 */
export class InteractionPanel {
  private readonly root = document.createElement('div');
  private readonly title = document.createElement('div');
  private readonly who = document.createElement('div');
  private readonly bar = document.createElement('div');
  private readonly fill = document.createElement('div');
  private readonly left = document.createElement('div');
  private readonly log = document.createElement('div');
  private readonly actions = document.createElement('div');

  /** Kiedy rozmowa się skończy, zegarem tego klienta. Brak przy wozie. */
  private deadline?: number;
  /** Ile sekund było na tę kwestię — mianownik paska. */
  private span = 1;
  /** Ostatnio wypisana liczba sekund — żeby nie przemalowywać co ramkę. */
  private painted?: number;

  constructor(
    container: HTMLElement,
    private readonly deps: {
      /** „Opuść rozmowę" — wyłącznie po stronie zagadującego. */
      onPart: () => void;
      /** Wybrana kwestia. Jedzie identyfikator, nie etykieta. */
      onSay: (action: string) => void;
    }
  ) {
    this.root.className = 'talk';
    this.root.hidden = true;

    this.title.className = 'talk-title';
    this.who.className = 'talk-who';
    this.bar.className = 'talk-bar';
    this.fill.className = 'talk-bar-fill';
    this.left.className = 'talk-left';
    this.log.className = 'talk-log';
    this.actions.className = 'talk-actions';

    this.bar.append(this.fill);
    this.root.append(this.title, this.who, this.left, this.bar, this.log, this.actions);
    container.append(this.root);
  }

  get open(): boolean {
    return !this.root.hidden;
  }

  /**
   * Pokaż to, co jest teraz — albo schowaj, gdy nie ma z kim rozmawiać.
   *
   * Jedno wejście na początek, każdą kwestię i koniec, bo taka przychodzi
   * wiadomość: pusty rozmówca znaczy „z nikim", a nie „nic się nie zmieniło".
   */
  show(talk: Talking): void {
    if (!talk.partner) {
      this.hide();
      return;
    }

    const cart = talk.partner.kind === 'convoy';
    this.title.textContent = cart ? 'Inspekcja' : 'Rozmowa';
    // Wóz jedzie bez podpisu — i tutaj też, bo nazwa, której nikt nie nadał,
    // byłaby nazwą wymyśloną przez klienta.
    this.who.textContent = talk.partner.name ?? 'wóz handlarza';

    const seconds = talk.seconds;
    this.deadline = seconds === null ? undefined : performance.now() + Math.max(0, seconds) * 1000;
    // Mianownik bierze się z ostatniej wiadomości, a nie ze stałej w tym
    // pliku: ile trwa jedna kwestia, wie serwer, i wolno mu to zmienić bez
    // pytania klienta o zdanie.
    this.span = Math.max(1, seconds ?? 1);
    this.painted = undefined;
    this.left.hidden = this.deadline === undefined;
    this.bar.hidden = this.deadline === undefined;

    this.paintLog(talk.log);
    this.paintActions(talk, cart);

    this.root.hidden = false;
    this.tick();
  }

  /**
   * Przebieg rozmowy: co padło i po czyjej stronie.
   *
   * Przepisywany w całości, bo w całości przychodzi — dopisywanie ostatniej
   * kwestii do tego, co już wisi, znaczyłoby drugi rachunek tego samego,
   * rozjeżdżający się z serwerem przy pierwszej zgubionej ramce.
   */
  private paintLog(lines: Talking['log']): void {
    this.log.replaceChildren();
    this.log.hidden = lines.length === 0;
    for (const line of lines) {
      const said = document.createElement('div');
      said.className = line.mine ? 'talk-line mine' : 'talk-line';
      said.textContent = line.label;
      this.log.append(said);
    }
  }

  /** Kwestie do wybrania, a pod nimi wyjście albo zdanie o zatrzymaniu. */
  private paintActions(talk: Talking, cart: boolean): void {
    this.actions.replaceChildren();

    for (const choice of talk.options) {
      const say = document.createElement('button');
      say.type = 'button';
      say.className = 'talk-action';
      say.textContent = choice.label;
      // Wyszarzenie jest uprzejmością, nie zamkiem: regułą jest ta na serwerze,
      // która odmówi kwestii wybranej nie w porę. Ta istnieje, żeby kliknięcie
      // nie kończyło się linią błędu za coś, czego jeszcze nie było widać.
      say.disabled = !choice.enabled;
      say.addEventListener('click', () => this.deps.onSay(choice.id));
      this.actions.append(say);
    }

    // **Czekanie dotyczy obu stron**, bo rozmowa idzie na przemian: kto
    // powiedział ostatnie zdanie, ma swoje kwestie wyszarzone, dopóki tamten
    // nie odpowie. Zdanie o tym stoi po obu stronach, bo wyszarzony przycisk
    // bez powodu wygląda jak zepsuty.
    const waiting = talk.options.length > 0 && talk.options.every((choice) => !choice.enabled);

    if (talk.mine) {
      if (waiting && !cart) {
        const held = document.createElement('div');
        held.className = 'talk-note';
        held.textContent = 'czekasz na odpowiedź';
        this.actions.append(held);
      }

      const leave = document.createElement('button');
      leave.type = 'button';
      leave.className = 'talk-action talk-leave';
      leave.textContent = cart ? 'Opuść inspekcję' : 'Opuść rozmowę';
      leave.addEventListener('click', () => this.deps.onPart());
      this.actions.append(leave);
    } else {
      // Zatrzymany nie ma wyjścia, i to jest treść zatrzymania, a nie
      // przeoczenie: gdyby miał, reguła trzydziestu sekund nie istniałaby.
      // Odezwać się wolno mu tak samo jak drugiej stronie — odejść nie.
      //
      // Zdanie mówi to, co w tej chwili najbardziej potrzebne: dopóki
      // odpowiedzi są zablokowane, to nie blokada ruchu jest tym, czego się nie
      // rozumie, tylko szare przyciski.
      const held = document.createElement('div');
      held.className = 'talk-note';
      held.textContent = waiting
        ? 'zatrzymano cię — teraz kolej na tamtego'
        : 'zatrzymano cię — nie możesz się ruszyć ani działać';
      this.actions.append(held);
    }
  }

  hide(): void {
    this.root.hidden = true;
    this.deadline = undefined;
    this.painted = undefined;
  }

  /**
   * Przepisz licznik i pasek, jeśli jest co przepisywać.
   *
   * Wołane z pętli rysowania, jak dymek nad budynkiem: własny `setInterval`
   * byłby drugim zegarem w kliencie, który ma jeden. Pasek jedzie przez
   * `transform`, a nie przez szerokość — przeliczany co ramkę, a przy skali
   * nie ma czego układać na nowo.
   */
  tick(): void {
    if (this.root.hidden || this.deadline === undefined) return;

    const left = Math.max(0, this.deadline - performance.now());
    this.fill.style.transform = `scaleX(${Math.min(1, left / (this.span * 1000))})`;

    const seconds = Math.ceil(left / 1000);
    if (seconds === this.painted) return;
    this.painted = seconds;
    this.left.textContent = `${seconds} s`;
  }

  dispose(): void {
    this.root.remove();
  }
}
